import type {
  AgentSelection,
  ChatState,
  MessageAttachment,
  ModelSelection,
  SessionState,
  ToolCallResult,
} from '@microsoft/agent-host-protocol'
import type {BuildInputCompletedActionParams} from './ahp-input-actions'
import type {ProvisioningStatus} from './session-host/resolve-project'
import type {
  RelayChatObserver,
  RelayModelCallFailure,
  RelayStatus,
  RelayTelemetryObserver,
} from './relay-connection-runtime'
import type {ObservableRelaySink, RelayEntry} from './relay-entry-types'
import type {RelaySteeringMessage} from './relay-steering-messages'

export interface RelayLeaseIdentity {
  /** Product task metadata, when the host has a task id for this session. */
  taskId?: string
  environmentId: string
  sessionId: string
  channel?: string
}

/**
 * A generation-bound relay capability for one mounted surface.
 *
 * The lease deliberately owns the command and observation methods used by the
 * established-session client. Callers do not need to pass a task id back into
 * the registry, so a late cleanup from an older identity cannot mutate a
 * replacement entry.
 */
export interface RelayLease {
  readonly identity: RelayLeaseIdentity
  readonly generation: number
  isCurrent(): boolean
  release(): void
  subscribe(listener: () => void): () => void
  getSnapshot(): ChatState | undefined
  getStatus(): RelayStatus
  getReachable(): boolean
  getError(): string | undefined
  getModelFailures(): Record<string, RelayModelCallFailure> | undefined
  getProvisioning(): ProvisioningStatus | undefined
  getSessionState(): SessionState | undefined
  getPassive(): boolean
  getPassiveKnown(): boolean
  getSteeringMessages(): readonly RelaySteeringMessage[]
  dispatchTurn(text: string, attachments?: MessageAttachment[], model?: ModelSelection, agent?: AgentSelection): string
  queueMessage(text: string, attachments?: MessageAttachment[], model?: ModelSelection, agent?: AgentSelection): string
  steerMessage(
    text: string,
    attachments?: MessageAttachment[],
    model?: ModelSelection,
    steeringId?: string,
    agent?: AgentSelection,
  ): string
  removeQueuedMessage(id: string): void
  cancelTurn(turnId?: string): boolean
  confirmToolCall(confirmation: {
    approved: boolean
    toolCallId: string
    turnId: string
    resultConfirmation?: boolean
    selectedOptionId?: string
    forSession?: boolean
  }): void
  completeToolCall(input: {turnId: string; toolCallId: string; result: ToolCallResult}): void
  renameSession(title: string): void
  completeInput(params: BuildInputCompletedActionParams): void
  setApproveAll(enabled: boolean): Promise<void>
  ensureLive(onReconnect?: () => void): Promise<boolean>
  whenConnected(timeoutMs?: number, provisioningTimeoutMs?: number): Promise<void>
  observeChat(channel: string, observer: RelayChatObserver): () => void
  subscribeTelemetry(observer: RelayTelemetryObserver): Promise<() => void>
}

const relayLeaseEntry = Symbol('relayLeaseEntry')

interface InternalRelayLease extends RelayLease {
  [relayLeaseEntry]: {
    entry: RelayEntry
    registryKey: string
    isReleased: () => boolean
  }
}

export interface RelayLeaseRegistryAccess {
  getRelayEntry(registryKey: string): RelayEntry | undefined
  releaseRelayEntry(registryKey: string, entry: RelayEntry): void
  subscribeRelayRegistry(listener: () => void): () => void
  getRelayStore(registryKey: string): ObservableRelaySink | undefined
  getRelayStatus(registryKey: string): RelayStatus
  getRelayReachable(registryKey: string): boolean
  getRelayError(registryKey: string): string | undefined
  getRelayModelFailures(registryKey: string): Record<string, RelayModelCallFailure> | undefined
  getRelaySessionState(registryKey: string): SessionState | undefined
  isRelayPassive(registryKey: string): boolean
  getRelaySteeringMessages(registryKey: string): readonly RelaySteeringMessage[]
  setRelayApproveAll(registryKey: string, enabled: boolean): Promise<void>
  ensureRelayLive(registryKey: string, onReconnect?: () => void): Promise<boolean>
  whenRelayConnected(registryKey: string, timeoutMs?: number, provisioningTimeoutMs?: number): Promise<void>
}

function leaseEntryOrThrow(lease: RelayLease, registry: RelayLeaseRegistryAccess): RelayEntry {
  const internal = lease as InternalRelayLease
  const metadata = internal[relayLeaseEntry]
  const current = registry.getRelayEntry(metadata.registryKey)
  if (metadata.isReleased() || current !== metadata.entry || current.generation !== lease.generation) {
    throw new Error('relay lease is stale')
  }
  return current
}

export function createRelayLease(
  entry: RelayEntry,
  identity: RelayLeaseIdentity,
  registryKey: string,
  registry: RelayLeaseRegistryAccess,
): RelayLease {
  let released = false
  const lease = {
    identity: Object.freeze({...identity}),
    generation: entry.generation,
    isCurrent: () => !released && registry.getRelayEntry(registryKey) === entry,
    release: () => {
      if (released) return
      released = true
      registry.releaseRelayEntry(registryKey, entry)
    },
    subscribe: (listener: () => void) => {
      leaseEntryOrThrow(lease, registry)
      const unsubscribeRegistry = registry.subscribeRelayRegistry(listener)
      const store = registry.getRelayStore(registryKey)
      const unsubscribeStore = store?.subscribe(listener)
      return () => {
        unsubscribeRegistry()
        unsubscribeStore?.()
      }
    },
    getSnapshot: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.getRelayStore(registryKey)?.getSnapshot()
    },
    getStatus: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.getRelayStatus(registryKey)
    },
    getReachable: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.getRelayReachable(registryKey)
    },
    getError: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.getRelayError(registryKey)
    },
    getModelFailures: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.getRelayModelFailures(registryKey)
    },
    getProvisioning: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.getRelayEntry(registryKey)?.provisioning
    },
    getSessionState: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.getRelaySessionState(registryKey)
    },
    getPassive: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.isRelayPassive(registryKey)
    },
    getPassiveKnown: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.getRelayEntry(registryKey)?.passiveKnown ?? false
    },
    getSteeringMessages: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.getRelaySteeringMessages(registryKey)
    },
    dispatchTurn: (text: string, attachments?: MessageAttachment[], model?: ModelSelection, agent?: AgentSelection) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (current.passive) throw new Error('relay connection is read-only')
      if (!current.runtime.dispatchTurn) throw new Error('active relay cannot dispatch turns')
      const turnId = current.runtime.dispatchTurn(text, attachments, model, agent)
      if (!turnId) throw new Error('active relay did not return a dispatched turn id')
      return turnId
    },
    queueMessage: (text: string, attachments?: MessageAttachment[], model?: ModelSelection, agent?: AgentSelection) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (current.passive) throw new Error('relay connection is read-only')
      if (!current.runtime.queueMessage) throw new Error('active relay cannot queue messages')
      return current.runtime.queueMessage(text, attachments, model, agent)
    },
    steerMessage: (
      text: string,
      attachments?: MessageAttachment[],
      model?: ModelSelection,
      steeringId?: string,
      agent?: AgentSelection,
    ) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (current.passive) throw new Error('relay connection is read-only')
      if (!current.runtime.steerMessage) throw new Error('active relay cannot steer messages')
      return current.runtime.steerMessage(text, attachments, model, steeringId, agent)
    },
    removeQueuedMessage: (id: string) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (current.passive) throw new Error('relay connection is read-only')
      if (!current.runtime.removeQueuedMessage) throw new Error('active relay cannot remove queued messages')
      current.runtime.removeQueuedMessage(id)
    },
    cancelTurn: (turnId?: string) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (current.passive || !current.runtime.cancelTurn) return false
      return current.runtime.cancelTurn(turnId)
    },
    confirmToolCall: (confirmation: {
      approved: boolean
      toolCallId: string
      turnId: string
      resultConfirmation?: boolean
      selectedOptionId?: string
      forSession?: boolean
    }) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (current.passive) throw new Error('relay connection is read-only')
      if (!current.runtime.confirmToolCall) throw new Error('active relay cannot confirm tool calls')
      current.runtime.confirmToolCall(confirmation)
    },
    completeToolCall: (input: {turnId: string; toolCallId: string; result: ToolCallResult}) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (current.passive) throw new Error('relay connection is read-only')
      if (!current.runtime.completeToolCall) throw new Error('active relay cannot complete tool calls')
      current.runtime.completeToolCall(input)
    },
    renameSession: (title: string) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (current.passive) throw new Error('relay connection is read-only')
      if (!current.runtime.renameSession) throw new Error('active relay cannot rename sessions')
      current.runtime.renameSession(title)
    },
    completeInput: (params: BuildInputCompletedActionParams) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (current.passive) throw new Error('relay connection is read-only')
      if (!current.runtime.completeInput) throw new Error('active relay cannot complete input requests')
      current.runtime.completeInput(params)
    },
    setApproveAll: (enabled: boolean) => {
      leaseEntryOrThrow(lease, registry)
      return registry.setRelayApproveAll(registryKey, enabled)
    },
    ensureLive: (onReconnect?: () => void) => {
      leaseEntryOrThrow(lease, registry)
      return registry.ensureRelayLive(registryKey, onReconnect)
    },
    whenConnected: (timeoutMs?: number, provisioningTimeoutMs?: number) => {
      leaseEntryOrThrow(lease, registry)
      return registry.whenRelayConnected(registryKey, timeoutMs, provisioningTimeoutMs)
    },
    observeChat: (channel: string, observer: RelayChatObserver) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (!current.runtime.observeChat) throw new Error('relay does not support additional chat subscriptions')
      return current.runtime.observeChat(channel, observer)
    },
    subscribeTelemetry: (observer: RelayTelemetryObserver) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (!current.runtime.subscribeTelemetry) throw new Error('relay does not support telemetry subscriptions')
      return current.runtime.subscribeTelemetry(observer)
    },
  } as InternalRelayLease

  lease[relayLeaseEntry] = {entry, registryKey, isReleased: () => released}
  return lease
}
